import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Compass } from "lucide-react";
import { useShoppingList } from "../contexts/ShoppingListContext";
import { useMeals } from "../contexts/UserMealsContext";
import { MealSuggestionApi } from "../api/MealSuggestionApi";
import { ExploreModeToggle } from "../components/ExplorePage/ExploreModeToggle";
import { AiFreeTextInputCard } from "../components/MealSuggestions/AiFreeTextInputCard";
import { MealAlternativesPicker } from "../components/ExplorePage/MealAlternativesPicker";
import { MealSuggestionCard } from "../components/MealSuggestions/MealSuggestionCard";

const DESCRIPTION_LOADING_PHRASES = [
  "Thinking up some ideas...",
  "Matching meals to your mood...",
  "Weighing up some options...",
  "Cooking up suggestions...",
];

const MODES = {
  description: {
    label: "Describe what you want",
    subtitle: "Tell us what you're in the mood for and we'll suggest meals.",
  },
  alternatives: {
    label: "Alternatives to a meal",
    subtitle: "Pick one of your recipes and we'll suggest something different.",
  },
};

export function ExplorePage() {
  const navigate = useNavigate();
  const { meals } = useMeals();
  const { addItemsToShoppingList } = useShoppingList();
  const [searchParams, setSearchParams] = useSearchParams();
  const [suggestions, setSuggestions] = useState(null);

  const requestedMode = searchParams.get("mode");
  const mode = MODES[requestedMode] ? requestedMode : "description";

  const handleModeChange = (nextMode) => {
    setSuggestions(null);
    setSearchParams(nextMode === "description" ? {} : { mode: nextMode });
  };

  const handleGenerate = async (text) => {
    const results = await MealSuggestionApi.suggestMeals(text);
    setSuggestions(results);
  };

  const handleGetAlternatives = async (mealId, note) => {
    const results = await MealSuggestionApi.suggestAlternatives(mealId, note);
    setSuggestions(results);
  };

  const handleSaveRecipe = (suggestion) => {
    navigate("/CreateMeal", { state: { aiDraft: suggestion } });
  };

  const handleAddMissingToList = (suggestion) => {
    const missingItems = suggestion.ingredients
      .filter((ing) => !ing.onHand)
      .map((ing) => ({
        itemName: ing.name,
        itemQuantity: ing.quantity,
        itemUnit: ing.unit,
      }));
    return addItemsToShoppingList(missingItems);
  };

  return (
    <div className="h-full overflow-auto p-4 sm:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Compass className="w-6 h-6 text-gray-900" />
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            Explore
          </h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">{MODES[mode].subtitle}</p>

        <ExploreModeToggle
          modes={MODES}
          activeMode={mode}
          onModeChange={handleModeChange}
        />

        {mode === "description" && (
          <AiFreeTextInputCard
            label="What are you in the mood for?"
            placeholder={`e.g. "quick vegan dinners under 30 minutes" or "something spicy and comforting"`}
            loadingPhrases={DESCRIPTION_LOADING_PHRASES}
            onGenerate={handleGenerate}
          />
        )}
        {mode === "alternatives" && (
          <MealAlternativesPicker
            meals={meals}
            onGetAlternatives={handleGetAlternatives}
          />
        )}

        {suggestions && (
          <>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
              Suggestions
            </p>
            {suggestions.length === 0 ? (
              <p className="text-sm text-gray-600">
                Couldn't find any meal ideas from that. Try again with a bit
                more detail.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {suggestions.map((suggestion, index) => (
                  <MealSuggestionCard
                    key={index}
                    suggestion={suggestion}
                    onSaveRecipe={() => handleSaveRecipe(suggestion)}
                    onAddMissingToList={() => handleAddMissingToList(suggestion)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
